import type { EnhancedFileCitation } from '../../types/chat.types';

type EnrichmentSchedulerDeps = {
  enhanceCitations: (
    citations: EnhancedFileCitation[],
  ) => Promise<EnhancedFileCitation[]>;
  getPendingCitations: (fileIds: Set<string>) => EnhancedFileCitation[];
  mergeEnhancedCitations: (enhanced: EnhancedFileCitation[]) => void;
  onStateUpdated: () => void;
  delayMs?: number;
};

type EnrichmentScheduler = {
  schedule: (fileId: string) => void;
  cancel: () => void;
};

const DEFAULT_DELAY_MS = 50;

export const createEnrichmentScheduler = ({
  enhanceCitations,
  getPendingCitations,
  mergeEnhancedCitations,
  onStateUpdated,
  delayMs = DEFAULT_DELAY_MS,
}: EnrichmentSchedulerDeps): EnrichmentScheduler => {
  const scheduled = new Set<string>();
  const inFlight = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = () => {
    if (timer !== null || scheduled.size === 0) {
      return;
    }

    timer = setTimeout(async () => {
      timer = null;

      const idsToFetch = Array.from(scheduled).filter(id => !inFlight.has(id));
      scheduled.clear();

      if (idsToFetch.length === 0) {
        if (scheduled.size > 0) {
          run();
        }
        return;
      }

      idsToFetch.forEach(id => inFlight.add(id));

      try {
        const pending = getPendingCitations(new Set(idsToFetch));
        if (pending.length > 0) {
          const enhanced = await enhanceCitations(pending);
          mergeEnhancedCitations(enhanced);
          onStateUpdated();
        }
      } catch {
        // Keep existing citations if enrichment fails.
      } finally {
        idsToFetch.forEach(id => inFlight.delete(id));
        if (scheduled.size > 0) {
          run();
        }
      }
    }, delayMs);
  };

  return {
    schedule: (fileId: string) => {
      if (!fileId) {
        return;
      }
      scheduled.add(fileId);
      run();
    },
    cancel: () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      scheduled.clear();
    },
  };
};
